import { useState } from 'react';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import { IconArrowRight, IconArrowBackUp, IconChevronDown, IconInfoCircle, IconRefresh, IconSparkles, IconUpload, IconX } from '@tabler/icons-react';
import { needsSchedule, type ServiceRow } from './svcmap';
import { BOARD_LTS } from './servicesBoardData';
import './ImpTab.i18n';
import s from './servicesmap.module.css';

// Mint-ServicesMap › view=imp (Nhập & đồng bộ). Số liệu mẫu của board; ghi lịch cần anh duyệt DB nên các nút ghi báo "chưa bật ghi".
type Tone = 'ok' | 'warn' | 'err' | 'link' | 'neutral';

type RecKind = 'ves' | 'etd' | 'cls' | 'dly';
const REC_KIND: Record<RecKind, [string, Tone]> = { ves: ['ip.kVes', 'link'], etd: ['ip.kEtd', 'warn'], cls: ['ip.kCls', 'warn'], dly: ['ip.kDly', 'err'] };
// [loại, service, tàu/chuyến, trường, cũ, mới, ghi chú, thao tác]
const REC0: readonly [RecKind, string, string, string, string, string, string, string][] = [
  ['ves', 'CV7 · MSK', 'Chuyến 643N · ETD 24/Oct', 'Tàu', 'MAERSK KEELUNG', 'MAERSK PORT KLANG', 'Loading Confirmation · lịch dài hạn ghi MAERSK KEELUNG 643N · cập nhật tàu cho chuyến này', 'Cập nhật lịch'],
  ['etd', 'VTX4 · RCL', 'PIYA BHUM 099N', 'ETD', '22/Oct', '24/Oct', 'ETD Change · lệch 2 ngày (> 1 ngày) · chờ duyệt · lịch dài hạn chưa đổi', 'Duyệt ETD mới'],
  ['cls', 'CKI · ONE', 'KMTC HAIPHONG 2608N', 'Closing', '24/Oct', '25/Oct', 'Closing Change · cut-off CY đổi · báo Booking và CS có job trên chuyến', 'Báo Booking'],
  ['dly', 'JTVS · CNC', 'CNC BANGKOK JTV050N89', 'ETD', '19/Oct', '21/Oct', 'Delay (7 lần) · ETD thực tế 21/Oct tại Cảng VICT · cảnh báo trễ cho job liên quan', 'Cập nhật ETD'],
  ['dly', 'EC3 · ONE', 'ONE BLUE JAY 043E', 'ETD', '21/Oct', '23/Oct', 'Delay (7 lần) · ETD 23/Oct tại VNVUT (TCIT) · cảnh báo trễ cho job liên quan', 'Cập nhật ETD'],
];
// [service, tàu, ETD cũ, ETD mới, lúc ghi, nguồn]
const AUTO0: readonly [string, string, string, string, string, string][] = [
  ['KHS1 · KMT', 'HEUNG-A HOCHIMINH 2613N', '14/Nov', '15/Nov', '24/Oct 08:05', 'API hãng'],
  ['VTX3 · SITC', 'SITC WENDE 2623N', '01/Nov', '31/Oct', '24/Oct 06:00', 'thông báo hãng'],
  ['CKI · ONE', 'KMTC HAIPHONG 2608N', '25/Oct', '26/Oct', '23/Oct 15:20', 'TblVesselData'],
];

type ImpKind = 'new' | 'etd' | 'ves' | 'omit' | 'dup' | 'unk';
const IMP_KIND: Record<ImpKind, [string, Tone]> = { new: ['ip.iNew', 'ok'], etd: ['ip.kEtd', 'warn'], ves: ['ip.kVes', 'link'], omit: ['Omit', 'err'], dup: ['ip.iDup', 'neutral'], unk: ['ip.iUnk', 'warn'] };
// [loại, service, tàu, cảng, ETD, ghi chú]
const IMP0: readonly [ImpKind, string, string, string, string, string][] = [
  ['new', 'KHS1', 'SAWASDEE INCHEON 2615N', 'VNSGN', '03/Jan/2027', 'Chuyến mới sau 27/Dec'],
  ['new', 'KHS1', 'KMTC BANGKOK 2616N', 'VNSGN', '10/Jan/2027', 'Chuyến mới'],
  ['etd', 'KHS1', 'HEUNG-A HOCHIMINH 2613N', 'VNSGN', '14/Nov → 15/Nov', 'ETD lùi 1 ngày'],
  ['ves', 'KCS', 'KMTC PENANG 2610N', 'VNSGN', '09/Dec', 'Thay STARSHIP MERCURY'],
  ['omit', 'KCS', 'KOTA GAYA 0434N', 'VNSGN', '05/Dec', 'OMIT: CNSHK'],
  ['dup', 'KCS', 'LOS ANDES BRIDGE 0248N', 'VNSGN', '16/Dec', 'Đã có · tự bỏ qua'],
  ['unk', '?', 'STARSHIP TAURUS 2611N', 'VNSGN', '02/Dec', 'Không khớp service · chọn tay'],
];

type Src = { key: string; k: string; tip: string; on0: boolean; st: 'ok' | 'err' | 'late'; ago: string; h7: number[]; err: string };
const SRC: readonly Src[] = [
  { key: 'vd', k: 'Thông báo hãng', tip: 'Loading Confirmation · Delay · ETD / Closing Change · 15 phút/lần', on0: true, st: 'ok', ago: '15 phút trước · 24/Oct 10:15', h7: [14, 9, 22, 17, 11, 19, 16], err: '' },
  { key: 'api', k: 'Lịch hãng trực tiếp', tip: 'ONE · MSK · CMA · HPL · 06:00 hằng ngày', on0: true, st: 'err', ago: 'Lỗi · 4 giờ trước · 24/Oct 06:00', h7: [42, 0, 38, 40, 0, 44, 31], err: 'HPL không phản hồi lần chạy 06:00 · ONE · MSK · CMA vẫn lấy được' },
  { key: 'web', k: 'Trang lịch hãng', tip: 'Đọc trang lịch hãng (LongScheduleLink) · Hana mở link lịch của từng service, so với lịch đã nhập · thứ Hai hằng tuần', on0: false, st: 'late', ago: 'Trễ · 4 ngày trước · 20/Oct 07:00', h7: [0, 0, 0, 0, 12, 0, 0], err: 'Đến hạn chạy thứ Hai 27/Oct' },
];
const SRC_ST = { ok: ['ip.sOk', 'ok'], late: ['ip.sLate', 'warn'], err: ['ip.sErr', 'err'] } as const;
const HIST: readonly [string, string, 'file' | 'ai' | 'api', string, string][] = [
  ['22/Oct 10:12', '2 ngày trước', 'file', 'KMTC_LTS_Nov.xlsx', '18 chuyến · KHS1 · NKT'],
  ['21/Oct 16:40', '3 ngày trước', 'ai', 'Ask HANA · ảnh Routing Network', '6 chuyến · SITC VTX3'],
  ['18/Oct 09:05', '6 ngày trước', 'api', 'ONE', '42 chuyến · 3 đổi ETD'],
  ['15/Oct 08:00', '9 ngày trước', 'file', 'ZIP CMA 4 file', 'AEU7 · MEX · PEX3'],
];

const toneCls = (t: Tone) => s[`tn_${t}`];
const spark = (h: readonly number[], w: number, hh: number) => {
  const mn = Math.min(...h), mx = Math.max(...h), r = mx - mn || 1;
  return h.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * (w - 4)) / (h.length - 1)).toFixed(1)} ${(hh - 3 - ((v - mn) / r) * (hh - 6)).toFixed(1)}`).join(' ');
};

interface Props {
  rows: readonly ServiceRow[];
  onView: (v: 'svc') => void;
}

export function ImpTab({ rows, onView }: Props) {
  const t = useT();
  const toast = useToast();
  const pending = () => toast({ msg: t('impt.noWrite'), tone: 'err' });
  const [recF, setRecF] = useState<'all' | 'wait' | 'auto'>('all');
  const [done, setDone] = useState<Record<number, true>>({});
  const [auto, setAuto] = useState(true);
  const [syncOn, setSyncOn] = useState<Record<string, boolean>>({});
  const [car, setCar] = useState('KMT');
  const [carOpen, setCarOpen] = useState(false);
  const [picked, setPicked] = useState(true);
  const [impK, setImpK] = useState<ImpKind | ''>('');
  const [off, setOff] = useState<Record<number, boolean>>({ 5: true });

  const cars = [...new Set(rows.flatMap((v) => v.cars))].sort();
  const on = (x: Src) => syncOn[x.key] ?? x.on0;
  const wait = REC0.map((r, i) => ({ r, i })).filter(({ i }) => !done[i]);
  const autoRows = auto ? AUTO0.map((r, i) => ({ r, i })) : [];
  const nAuto = autoRows.filter(({ r }) => r[4].startsWith('24/Oct')).length;
  const nAutoShown = autoRows.length;
  const nErr = SRC.filter((x) => on(x) && x.st === 'err').length;
  const due = rows
    .filter((v) => v.active && needsSchedule(v))
    .map((v) => {
      const fut = (BOARD_LTS[v.code] ?? []).slice(1);
      let wl = fut.findIndex((x) => x === '>' || x === '-');
      if (wl < 0) wl = fut.length;
      const wMax = fut.length || 8;
      return { v, wl, end: v.status === 'end', w: `${Math.max(4, Math.round((wl / wMax) * 100))}%` };
    })
    .sort((a, b) => a.wl - b.wl);
  const kpis = [
    { k: t('ip.wait'), v: wait.length, sub: t('ip.waitSub'), tone: wait.length ? s.kpiWarn : '', go: () => setRecF('wait') },
    { k: t('ip.autoToday'), v: nAuto, sub: t('ip.autoSub'), tone: nAuto ? s.kpiOk : '', go: () => setRecF('auto') },
    { k: t('ip.srcErr'), v: nErr, sub: SRC.filter((x) => on(x) && x.st === 'err').map((x) => `${x.k} · ${x.err.split(' · ')[0]}`).join(' / ') || t('ip.allGood'), tone: nErr ? s.kpiErr : '', go: () => toast({ msg: nErr ? t('ip.hplDown') : t('ip.allOk') }) },
    { k: t('ip.dueSvc'), v: due.length, sub: t('ip.dueSub'), tone: due.length ? s.kpiErr : '', go: () => onView('svc') },
  ];

  const impRows = IMP0.map((r, i) => ({ r, i })).filter(({ r }) => !impK || r[0] === impK);
  const nImpOn = IMP0.filter((r, i) => !off[i] && r[0] !== 'dup').length;
  const impSum: { key: ImpKind | ''; k: string; n: number; tone: Tone | '' }[] = [
    { key: '', k: t('impt.all'), n: IMP0.length, tone: '' },
    ...(Object.keys(IMP_KIND) as ImpKind[]).map((k) => ({ key: k, k: t(IMP_KIND[k][0]), n: IMP0.filter((r) => r[0] === k).length, tone: IMP_KIND[k][1] })).filter((x) => x.n),
  ];

  const recList = [
    ...(recF === 'auto' ? [] : wait.map(({ r, i }) => ({ id: `w${i}`, i, auto: false, kind: REC_KIND[r[0]], svc: r[1], ves: r[2], fld: r[3], old: r[4], nw: r[5], tip: r[6], ak: r[7] }))),
    ...(recF === 'wait' ? [] : autoRows.map(({ r, i }) => ({ id: `a${i}`, i, auto: true, kind: ['ip.kEtd', 'warn'] as [string, Tone], svc: r[0], ves: r[1], fld: 'ETD', old: r[2], nw: r[3], tip: t('ip.autoTip', { at: r[4], src: r[5] }), ak: '' }))),
  ];
  const recTabs = [['all', t('impt.all'), wait.length + nAutoShown], ['wait', t('ip.wait'), wait.length], ['auto', t('ip.autoDone'), nAutoShown]] as const;

  return (
    <>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <button key={k.k} type="button" className={s.kpi} title={k.sub} aria-label={`${k.k} ${k.v}`} onClick={k.go}>
            <span className={s.kpiK}>{k.k}</span>
            <b className={k.tone}>{k.v}</b>
          </button>
        ))}
      </div>

      <section className={s.card} aria-label={t('ip.sync')}>
        <div className={s.cardHead}>
          <h2>{t('ip.sync')}</h2>
          <span className={s.grow} />
          <span className={s.cardNote}>{t('ip.rows7')}</span>
          <button type="button" className={s.cbtn} title={t('ip.lastSync', { at: '24/Oct 08:05' })} onClick={pending}><IconRefresh size={14} aria-hidden="true" />{t('ip.syncNow')}</button>
        </div>
        <div className={s.srcRow}>
          {SRC.map((x) => {
            const o = on(x);
            const n7 = x.h7.reduce((a, b) => a + b, 0);
            const [stK, stT] = SRC_ST[x.st];
            const tone: Tone = o ? stT : 'neutral';
            return (
              <div key={x.key} className={s.src} title={x.tip + (x.err ? ` · ${x.err}` : '')} style={{ opacity: o ? 1 : 0.72 }}>
                <div className={s.srcTop}>
                  <span role="img" aria-label={o ? t(stK) : t('ip.off')} className={`${s.dot} ${toneCls(tone)}`} />
                  <b>{x.k}</b>
                  <button type="button" role="switch" aria-checked={o} aria-label={t('ip.enable', { v: x.k })} className={s.sw} onClick={() => setSyncOn({ ...syncOn, [x.key]: !o })} />
                </div>
                <div className={s.srcBot}>
                  <span className={`${s.srcAgo} ${toneCls(tone)}`}>{o ? x.ago : t('ip.offLast', { v: x.ago.split(' · ').pop() ?? '' })}</span>
                  <svg role="img" aria-label={t('ip.spark', { k: x.k, n: n7, h: x.h7.join(', ') })} width="72" height="24" viewBox="0 0 72 24" className={o ? s.spOn : s.spOff}>
                    <path d={spark(x.h7, 72, 24)} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <b className={s.srcN}>{n7}</b>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className={s.impCols}>
        <div className={s.impMain}>
          <section className={s.card} aria-label={t('ip.import')}>
            <div className={s.cardHead}>
              <h2>{t('ip.import')}</h2>
              <span role="img" className={s.info} aria-label={t('ip.importTip')} title={t('ip.importTip')}><IconInfoCircle size={15} aria-hidden="true" /></span>
              <span className={s.grow} />
              <div className={s.ddWrap}>
                <button type="button" className={s.cbtn} aria-haspopup="listbox" aria-expanded={carOpen} onClick={() => setCarOpen(!carOpen)}>{t('ip.carrierSel', { v: car })}<IconChevronDown size={12} aria-hidden="true" /></button>
                {carOpen && (
                  <div role="listbox" aria-label={t('ip.carrier')} className={s.carPop}>
                    {cars.map((c) => (
                      <button key={c} type="button" role="option" aria-selected={c === car} className={s.carOpt} onClick={() => { setCar(c); setCarOpen(false); }}>{c}</button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" className={`${s.cbtn} ${s.cbtnAi}`} title={t('ip.aiTip')} onClick={pending}><IconSparkles size={16} aria-hidden="true" />{t('ip.aiBtn')}</button>
            </div>
            <button type="button" className={`${s.drop} ${picked ? s.dropDone : ''}`} onClick={() => { setPicked(true); setOff({ 5: true }); setImpK(''); }}>
              <IconUpload size={20} aria-hidden="true" />
              <b>{picked ? `${car}_LTS_Dec2026.xlsx` : t('ip.pickFile')}</b>
              <span>{picked ? t('ip.rowsN', { n: 7 }) : 'Excel · CSV · ZIP'}</span>
            </button>
            {picked && (
              <>
                <div role="toolbar" aria-label={t('ip.byKind')} title={t('ip.draft', { v: `${car}_LTS_Dec2026.xlsx` })} className={s.sumRow}>
                  {impSum.map((x) => (
                    <button key={x.k} type="button" aria-pressed={impK === x.key} className={`${s.sumChip} ${x.tone ? toneCls(x.tone) : ''}`} onClick={() => setImpK(impK === x.key ? '' : x.key)}>
                      <i aria-hidden="true" />{x.k}<b>{x.n}</b>
                    </button>
                  ))}
                </div>
                <div role="list" aria-label={t('ip.preview')}>
                  {impRows.map(({ r: [k, sc, ves, pc, etd, note], i }) => (
                    <div key={i} role="listitem" title={`${t(IMP_KIND[k][0])} · ${note}`} className={s.impRow} style={{ opacity: off[i] ? 0.55 : 1 }}>
                      <input type="checkbox" checked={!off[i]} aria-label={t('ip.pickWrite', { v: ves })} onChange={() => setOff({ ...off, [i]: !off[i] })} />
                      <span className={s.kindW}><span className={`${s.stChip} ${toneCls(IMP_KIND[k][1])}`}>{t(IMP_KIND[k][0])}</span></span>
                      <b className={s.impSc}>{sc}</b>
                      <span className={s.impVes}>{ves}</span>
                      <span className={s.impPc}>{pc}</span>
                      <span className={s.impEtd}>{etd}</span>
                      <span className={s.impNote}>{note}</span>
                    </div>
                  ))}
                </div>
                <div className={s.impFoot}>
                  <span className={s.cardNote}>{t('ip.draft', { v: `${car}_LTS_Dec2026.xlsx` })}</span>
                  <span className={s.grow} />
                  <button type="button" className={s.cbtn} onClick={() => setPicked(false)}><IconX size={16} aria-hidden="true" />{t('ip.cancel')}</button>
                  <button type="button" className={s.cbtnPri} onClick={pending}>{t('ip.confirm', { n: nImpOn })}</button>
                </div>
              </>
            )}
          </section>

          <section className={s.card} aria-label={t('ip.recon')}>
            <div className={s.cardHead}>
              <h2>{t('ip.recon')}</h2>
              <span role="img" className={s.info} aria-label={t('ip.reconSrc')} title={t('ip.reconSrc')}><IconInfoCircle size={15} aria-hidden="true" /></span>
              <div className={s.seg} role="tablist" aria-label={t('ip.reconFilter')}>
                {recTabs.map(([k, l, n]) => (
                  <button key={k} type="button" role="tab" aria-selected={recF === k} className={s.segBtn} onClick={() => setRecF(k)}>{l}<span>{n}</span></button>
                ))}
              </div>
              <span className={s.grow} />
              <span className={s.autoWrap} title={t('ip.autoRule')}>
                <span className={s.cardNote}>{t('ip.autoLabel')}</span>
                <button type="button" role="switch" aria-checked={auto} aria-label={t('ip.autoAria')} className={s.sw} onClick={() => setAuto(!auto)} />
              </span>
            </div>
            {!auto && <div className={s.autoOff}>{t('ip.autoOff')}</div>}
            {wait.length === 0 && recF !== 'auto' && <div className={s.recNone}>{t('ip.noDiff')}</div>}
            <div role="list" aria-label={t('ip.diffs')}>
              {recList.map((r) => {
                const [stK, stT] = r.auto ? (['ip.autoDone', 'ok' as Tone]) : ['ip.wait', 'warn' as Tone];
                return (
                  <div key={r.id} role="listitem" title={r.tip} className={s.recRow}>
                    <span className={s.kindW}><span className={`${s.stChip} ${toneCls(r.kind[1])}`}>{t(r.kind[0])}</span></span>
                    <b className={s.recSvc}>{r.svc}</b>
                    <span className={s.recVes}>{r.ves}</span>
                    <span role="img" aria-label={t('ip.change', { f: r.fld, a: r.old, b: r.nw })} className={s.recCh}>
                      <span className={s.cardNote}>{r.fld}</span>
                      <span className={s.recOld}>{r.old}</span>
                      <IconArrowRight size={14} aria-hidden="true" />
                      <b className={`${s.stChip} ${toneCls(r.kind[1])}`}>{r.nw}</b>
                    </span>
                    <span className={s.recSt}><span className={`${s.recStIn} ${toneCls(stT)}`}><i aria-hidden="true" />{t(stK)}</span></span>
                    <div className={s.recAct}>
                      {r.auto ? (
                        <button type="button" className={s.rbtn} aria-label={t('ip.undoAria', { v: `${r.svc} · ${r.ves}` })} onClick={pending}><IconArrowBackUp size={16} aria-hidden="true" />{t('ip.undo')}</button>
                      ) : (
                        <>
                          <button type="button" className={s.rbtnGhost} aria-label={t('ip.skipAria', { v: `${r.svc} · ${r.ves}` })} onClick={() => setDone({ ...done, [r.i]: true })}><IconX size={16} aria-hidden="true" />{t('ip.skip')}</button>
                          <button type="button" className={s.rbtn} aria-label={`${r.ak === 'Báo Booking' ? t('ip.notify') : t('ip.approve')} · ${r.svc} · ${r.ves}`} onClick={pending}>{r.ak === 'Báo Booking' ? t('ip.notify') : t('ip.approve')}</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <div className={s.impSide}>
          <section className={s.card} aria-label={t('ip.due')}>
            <div className={s.cardHead}>
              <h2>{t('ip.due')}</h2>
              <span className={s.grow} />
              {([['err', 'ip.ended'], ['warn', 'ip.soon']] as const).map(([tn, l]) => (
                <span key={tn} className={`${s.lg} ${toneCls(tn)}`}><i aria-hidden="true" />{t(l)}</span>
              ))}
            </div>
            {due.length === 0 && <div className={s.recNone}>{t('ip.allCovered')}</div>}
            {due.map((d) => (
              <div key={d.v.code} className={s.dueRow} title={`${d.end ? t('ip.endedAfter', { v: d.v.last }) : t('ip.until', { v: d.v.last })} · ${d.v.cars.join(' ')}`}>
                <button type="button" className={s.dueCode} aria-label={t('ip.openSvc', { v: d.v.code })} onClick={() => onView('svc')}>{d.v.code}</button>
                <div role="img" aria-label={t('ip.weeksLeft', { c: d.v.code, n: d.wl, v: d.v.last })} className={s.dueBar}><div className={toneCls(d.end ? 'err' : 'warn')} style={{ width: d.w }} /></div>
                <span className={`${s.dueWk} ${toneCls(d.end ? 'err' : 'warn')}`}>{t('ip.weeks', { n: d.wl })}</span>
                <button type="button" className={s.dueImp} aria-label={t('ip.importFor', { a: d.v.cars[0] ?? '', b: d.v.code })} title={t('ip.importFor', { a: d.v.cars[0] ?? '', b: d.v.code })} onClick={() => { setCar(d.v.cars[0] ?? car); setPicked(false); }}><IconUpload size={14} aria-hidden="true" /></button>
              </div>
            ))}
            <div className={s.dueAxis}><span>0</span><span>4</span><span>{t('ip.weeks', { n: 8 })}</span></div>
          </section>
          <section className={s.card} aria-label={t('ip.recent')}>
            <h2 className={s.h2}>{t('ip.recent')}</h2>
            <div role="list" aria-label={t('ip.recent')}>
              {HIST.map(([at, ago, kd, title, meta], i) => (
                <div key={at} role="listitem" className={s.hist}>
                  <div className={s.histMark}>
                    {kd === 'ai' ? <IconSparkles size={16} role="img" aria-label="Ask HANA" /> : <i className={kd === 'api' ? s.hOk : s.hPri} aria-hidden="true" />}
                    {i < HIST.length - 1 && <u aria-hidden="true" />}
                  </div>
                  <div className={s.histBody}>
                    <div><b>{title}</b><span title={at}>{ago}</span></div>
                    <div className={s.cardNote}>{meta}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
